import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import api from '../services/api';
import type { FeatureConfig } from '../types';

interface FeatureContextType {
  config: FeatureConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const FeatureContext = createContext<FeatureContextType | undefined>(undefined);

/* eslint-disable react-refresh/only-export-components */
export const useFeatureConfig = (): FeatureContextType => {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatureConfig must be used within a FeatureProvider');
  }
  return context;
};

interface FeatureProviderProps {
  children: ReactNode;
}

export const FeatureProvider: React.FC<FeatureProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<FeatureConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<FeatureConfig>('/api/features');
      if (response.success && response.data) {
        setConfig(response.data);
      } else {
        setError(response.error?.message || 'Failed to load feature config');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feature config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const value: FeatureContextType = {
    config,
    loading,
    error,
    refetch: fetchConfig,
  };

  return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
};
